import { stemOf } from './sidecar';

/** DICOM LO: what the Series Description holds. */
export const IMAGE_NAME_MAX = 64;

/**
 * The Name a new image starts with: its Series Description, what the viewer's
 * image cards show. The file's name without its extension (`CMU-1.ndpi` is
 * `CMU-1`, an OME-TIFF loses its `.ome` too); a slide of several files is
 * named for the slide already (its folder, its .mrxs) and is taken as it is.
 * Trimmed, and cut to what the field holds. The person may type anything over it.
 */
export function imageNameOf(name: string, slide = false): string {
  const stem = slide ? name : /\.ome\.tiff?$/i.test(name) ? name.replace(/\.ome\.tiff?$/i, '') : stemOf(name);
  return stem.trim().slice(0, IMAGE_NAME_MAX).trim();
}

// ── the Name's default (enterprise) ─────────────────────────────────────────
// An image's Name is, in order: what a person typed (never replaced); a series
// description a DICOM header or a metadata file said; its Specimen ID, when
// it has one; its file's name. The last two are DEFAULTS, and a default Name
// follows the Specimen ID as it appears, changes or is cleared. Whether the
// Name is still a default is read from the Name itself (it is one of them),
// so nothing about it is stored. Community has no specimen IDs: its Name is
// the file's (community/upload/store.ts).

/** The file an image is named for: its name, and whether it is a slide of several files (imageNameOf). */
export interface NamedFile {
  name: string;
  slide?: boolean;
}

/** A Specimen ID as a Name: trimmed, and cut to what the field holds; '' for none. */
export function specimenNameOf(specimenId: string): string {
  return specimenId.trim().slice(0, IMAGE_NAME_MAX).trim();
}

/** The Name an image has by default: its Specimen ID when there is one, else its file's name. */
export function defaultNameOf(specimenId: string, file: NamedFile): string {
  return specimenNameOf(specimenId) || imageNameOf(file.name, file.slide);
}

/** Whether a Name is still a default: empty, its file's, or the one `specimenId` gives. Anything else was typed, or said by a file. */
export function isDefaultName(name: string, specimenId: string, file: NamedFile): boolean {
  if (!name.trim()) return true;
  const specimen = specimenNameOf(specimenId);
  return name === imageNameOf(file.name, file.slide) || (specimen !== '' && name === specimen);
}

/**
 * The form after a change — typed, or what a DICOM header, a metadata file or
 * the server said — its Name following the Specimen ID: when the change gives
 * a new Specimen ID (or clears it) and the Name was a default before it, the
 * Name becomes the new default. A change that sets the Name itself keeps that
 * Name (a file's series description beats its Specimen ID), and a Name that
 * was no default is the person's: nothing changes it afterwards.
 * `after` itself when the Name stays.
 */
export function followName<D extends { seriesDescription: string; specimenId: string }>(before: D, after: D, file: NamedFile): D {
  if (after.seriesDescription !== before.seriesDescription) return after;
  if (specimenNameOf(after.specimenId) === specimenNameOf(before.specimenId)) return after;
  if (!isDefaultName(before.seriesDescription, before.specimenId, file)) return after;
  const name = defaultNameOf(after.specimenId, file);
  return name === after.seriesDescription ? after : { ...after, seriesDescription: name };
}
